import { useParams, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Logo } from "@/components/Logo";
import { ArrowLeft, Share2, BookOpen, Code2, Clock } from "lucide-react";
import type { Documentation } from "@shared/schema";
import { getDocumentation } from "@/lib/blogFirestore";

const TYPE_META: Record<string, { label: string; icon: typeof BookOpen; gradient: string }> = {
  "user-documentation": { label: "User Guide", icon: BookOpen, gradient: "from-[#8B2121] to-[#631212]" },
  "developer-documentation": { label: "Developer Docs", icon: Code2, gradient: "from-slate-600 to-slate-800" },
};

function formatDate(dateStr: string | Date | null) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

export default function DocPage() {
  const { type } = useParams<{ type: string }>();
  const [, setLocation] = useLocation();

  const { data: doc, isLoading } = useQuery<Documentation | null>({
    queryKey: ["firestore/documentation", type],
    queryFn: () => getDocumentation(type || ""),
    enabled: !!type,
  });

  const meta = TYPE_META[type || ""] || TYPE_META["user-documentation"];
  const Icon = meta.icon;

  const handleShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: doc?.title || meta.label, url: window.location.href }); } catch {}
    } else {
      await navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF2] dark:bg-[#1A1614]">
      <header className="sticky top-0 z-30 bg-[#FFFBF2]/90 dark:bg-[#1A1614]/90 backdrop-blur-md border-b border-[#8B2121]/10 dark:border-white/10 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Logo size="md" />
          <button
            onClick={handleShare}
            data-testid="button-share-doc"
            className="p-2 rounded-xl text-slate-500 hover:text-[#8B2121] transition-colors"
          >
            <Share2 className="h-5 w-5" />
          </button>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 pb-16">
        <button
          onClick={() => setLocation("/documentation")}
          data-testid="button-back-docs"
          className="flex items-center gap-1.5 text-sm font-medium text-slate-500 dark:text-slate-400 mb-6 hover:text-[#8B2121] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Documentation
        </button>

        {isLoading ? (
          <div className="space-y-4 animate-pulse">
            <div className="h-16 bg-slate-100 dark:bg-slate-800 rounded-3xl" />
            <div className="h-8 bg-slate-100 dark:bg-slate-800 rounded w-3/4" />
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => <div key={i} className="h-4 bg-slate-100 dark:bg-slate-800 rounded" />)}
            </div>
          </div>
        ) : !doc ? (
          <div className="text-center py-20 text-slate-400">
            <Icon className="h-14 w-14 mx-auto mb-3 opacity-40" />
            <p className="font-bold text-lg text-slate-700 dark:text-slate-300">Not yet available</p>
            <p className="text-sm mt-1">This documentation hasn't been written yet. Check back soon.</p>
          </div>
        ) : (
          <>
            <div className={`h-28 rounded-3xl bg-gradient-to-br ${meta.gradient} flex items-center px-6 gap-4 mb-6`}>
              <Icon className="h-10 w-10 text-white/80 flex-shrink-0" />
              <div>
                <p className="text-white/70 text-sm font-medium">{meta.label}</p>
                <h1 className="text-2xl font-bold text-white leading-tight">{doc.title}</h1>
              </div>
            </div>

            {doc.updatedAt && (
              <div className="flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500 mb-6">
                <Clock className="h-3.5 w-3.5" />
                Last updated {formatDate(doc.updatedAt)}
              </div>
            )}

            <div
              className="prose prose-slate max-w-none dark:prose-invert prose-headings:font-bold prose-a:text-[#8B2121] prose-img:rounded-2xl"
              dangerouslySetInnerHTML={{ __html: doc.content }}
            />
          </>
        )}
      </div>
    </div>
  );
}
